/**
 * saveQuotePayload — PURE projection of a saved quote (rate card OR booking
 * confirmation) into the { quote, checked, charges, insuranceQuote } shape the
 * coded `saveQuote` op on pf-admin-write persists (the coded replacement for the
 * legacy low-code action 4c86ee89 + its "Save quote charges" / "helper Action"
 * subs). One pure builder, jest-matrix tested (saveQuotePayload.test.js) — mirrors
 * the rfqPayload.js pattern.
 *
 * It reproduces the low-code's behaviour (transcribed in
 * notes/savequote-lowcode-transcription.md) and adds the two sections it couldn't
 * persist — CUSTOMS + WAREHOUSE:
 *   - Quote header from the card/booking (relations by id, mode Sea/Air, fclQuant_*,
 *     validity, ETD, currency, totals, checked* flags).
 *   - FREIGHT charges PER container size (FCL) with the price20/40/40hq/45 split +
 *     containerSize member + per-size weight, OR a single CBM row (LCL/Air).
 *   - One charge row per non-freight section (origin/destination/pickup/delivery +
 *     customs/warehouse), each carrying isEstimate + accessorialCharge.
 *   - Insurance rows (inline premium) and/or links to existing policy records.
 *
 * The caller (route_actions.save_final_form for the card; FInalView for the
 * booking) builds the normalized INPUT below; this util owns the projection so the
 * two surfaces share one tested code path (DRY).
 */

// ── enums / dicts (no string-matching) ───────────────────────────────────────

// RFQ section → the live Quote charge bucket back-FK key the op writes into.
// Mirrors pf-admin-write CHARGE_BUCKETS (ERS has no live bucket → not emitted).
export const SECTION_TO_BUCKET = {
  FREIGHT: 'chargesFreight',
  ORIGIN: 'chargesOrigin',
  DESTINATION: 'chargesDestination',
  PICKUP: 'chargesPickup',
  DELIVERY: 'chargesDelivery',
  CUSTOMS: 'chargesCustoms',
  WAREHOUSE: 'chargesWarehouse',
};

const CHARGE_BUCKETS = Object.values(SECTION_TO_BUCKET);

// bucket "chargesFreight" → Quote checked flag "checkedFreight".
const bucketToCheckedFlag = (bucket) => `checked${bucket.slice('charges'.length)}`;

// FCL size key → { Quote.fclQuant_* column, Charges.containerSize List member,
// Charges.price* split column }. The members (20ST/40ST/40HQ/45HC) are the ones
// the legacy low-code wrote (the RFQ path's "20"/"40HQ" 400'd, so it dropped them).
const SIZE_META = {
  '20': { fclQuant: 'fclQuant_20', member: '20ST', priceCol: 'price20' },
  '40': { fclQuant: 'fclQuant_40', member: '40ST', priceCol: 'price40' },
  '40HQ': { fclQuant: 'fclQuant_40hq', member: '40HC', priceCol: 'price40hq' },
  '45': { fclQuant: 'fclQuant_45', member: '45HC', priceCol: 'price45' },
};

const MODE = { OCEAN: 'OCEAN', AIR: 'AIR' };
const LOAD = { FCL: 'FCL', LCL: 'LCL' };

// ── small helpers ─────────────────────────────────────────────────────────────

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n) => Math.round((num(n) + Number.EPSILON) * 100) / 100;
const posInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
};
// id → number or null. BB record ids are always integers, so a non-numeric value
// is bad data and must not leak to the op's rel(toInt()) — return null, not the raw value.
const idOrNull = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// One non-freight section charge → a Charges row. amount is the per-unit SELL
// (the card already shows sell), so chargeAmount = amount × quantity.
function sectionChargeRow(charge, { isEstimate, currency }) {
  const quantity = posInt(charge.quantity) || 1;
  const unitCost = round2(charge.amount);
  return {
    chargetypes: idOrNull(charge.chargeTypeId),
    chargeName: charge.description || null,
    unitCode: charge.basis || null, // EA / CBM / KG / CTN (op guards the List)
    quantity,
    unitCost,
    chargeAmount: round2(unitCost * quantity),
    currency: charge.currency || currency || null,
    comments: charge.notes || null,
    isEstimate: Boolean(isEstimate),
    accessorialCharge: Boolean(charge.accessorial),
  };
}

// FCL freight → one Charges row PER picked size, with the per-size price split +
// containerSize member + per-size weight (low-code parity). LCL/Air → one CBM row.
function freightRows({ mode, containerType, fclSizes, chargeableWeight, freightRate, freightChargeTypeId, isEstimate, currency }) {
  if (containerType === LOAD.FCL) {
    return (Array.isArray(fclSizes) ? fclSizes : [])
      .filter((s) => SIZE_META[s.size] && posInt(s.qty) > 0 && num(s.price) > 0)
      .map((s) => {
        const meta = SIZE_META[s.size];
        const qty = posInt(s.qty);
        const price = round2(s.price);
        return {
          chargetypes: idOrNull(freightChargeTypeId),
          chargeName: 'Ocean Freight',
          unitCode: 'CTN',
          quantity: qty,
          unitCost: price,
          chargeAmount: round2(price * qty),
          currency: currency || null,
          // containerSize: meta.member,
          weight: num(s.weight) || null,
          [meta.priceCol]: price,
          isEstimate: Boolean(isEstimate),
          accessorialCharge: false,
        };
      });
  }
  // LCL / Air: a single lump row. `freightRate` is ALREADY the line TOTAL (per-unit rate ×
  // chargeableWeight — see getLclBookPrice/getAirBookPrice), so it IS the chargeAmount; the
  // per-unit cost is back-computed for the unit-cost × qty display. Air bills per KG (chargeable
  // weight in kg), LCL per CBM (chargeable volume). Multiplying by qty again here double-counts.
  const total = round2(freightRate);
  if (total > 0) {
    const qty = num(chargeableWeight) > 0 ? num(chargeableWeight) : 1;
    return [
      {
        chargetypes: idOrNull(freightChargeTypeId),
        chargeName: mode === MODE.AIR ? 'Air Freight' : 'LCL Freight',
        unitCode: mode === MODE.AIR ? 'KG' : 'CBM',
        quantity: qty,
        unitCost: round2(total / qty),
        chargeAmount: total,
        currency: currency || null,
        isEstimate: Boolean(isEstimate),
        accessorialCharge: false,
      },
    ];
  }
  return [];
}

// Insured-commodity row → an Insurance row (matches pf-admin-write insuranceInput).
function insuranceRow(item) {
  return {
    commodityCategory: item.category || null,
    commodityDescription: item.description || null,
    commodityValue: num(item.value) || null,
    premium: num(item.premium) || null,
    profitPercent: num(item.profitPercent) || null,
    policyReference: idOrNull(item.policyReference),
  };
}

// Per-leg pricing type chosen on the card (pickup/delivery 3-mode radio, #75 c14/c15).
export const LEG_MODE = { INSTANT: 'INSTANT_RATE', ESTIMATE: 'ESTIMATE', RFQ: 'RFQ' };

// Normalize a raw card / booking section-charge row into the shape sectionChargeRow wants.
// Tolerates the card vocab (getSelectedSectionData: charge_description / displayed_total /
// price / unit_code / chargetype_id) AND the builder vocab. `amount` is the DISPLAYED line
// total (displayed_total) so the saved charge equals what the customer saw; it falls back to
// the per-unit price only when no computed total was captured. fallbackChargeTypeId is the
// card's freight charge type, used only when a row carries no chargetype of its own.
export function adaptSectionCharges(arr, fallbackChargeTypeId) {
  return (Array.isArray(arr) ? arr : []).map((c) => ({
    chargeTypeId: c.chargetype_id ?? c.charge_type_id ?? c.chargeTypeId ?? fallbackChargeTypeId,
    description: c.charge_description ?? c.chargeName ?? c.name ?? c.description,
    basis: c.unit_code ?? c.basis ?? c.unitCode,
    amount: c.displayed_total ?? c.price ?? c.unitCost ?? c.amount ?? c.chargeAmount,
    quantity: c.quantity ?? c.qty,
    currency: c.currency,
    notes: c.notes ?? c.comments,
    // drayage accessorial lines carry `accessorial_charge` (drayageAccessorialMapping); keep it.
    accessorial: c.accessorial ?? c.accessorialCharge ?? c.accessorial_charge,
  }));
}

// Non-freight section buckets from the input's `sections` map, applying the per-leg TYPE rule:
//   RFQ leg      → save NOTHING (empty bucket; its checked flag stays false) — no firm price yet.
//   ESTIMATE leg → save rows flagged is_estimate=true.
//   INSTANT_RATE → save normally.
// Only PICKUP/DELIVERY carry a leg mode; other sections fall back to their estimates[] flag.
function sectionChargeBuckets(sections, estimates, currency, legModes) {
  const out = {};
  for (const [section, bucket] of Object.entries(SECTION_TO_BUCKET)) {
    if (section === 'FREIGHT') continue;
    const mode = legModes[section];
    if (mode === LEG_MODE.RFQ) {
      out[bucket] = []; // RFQ → nothing saved into the quote
      continue;
    }
    const isEstimate = mode === LEG_MODE.ESTIMATE || Boolean(estimates[section]);
    out[bucket] = (Array.isArray(sections[section]) ? sections[section] : [])
      .filter((c) => num(c.amount) !== 0 || c.description)
      .map((c) => sectionChargeRow(c, { isEstimate, currency }));
  }
  return out;
}

// FCL container counts → Quote header fclQuant_* fields (empty for LCL/Air).
function fclQuantsFor(containerType, fclSizes) {
  const out = {};
  if (containerType !== LOAD.FCL) return out;
  for (const s of Array.isArray(fclSizes) ? fclSizes : []) {
    const meta = SIZE_META[s.size];
    if (meta && posInt(s.qty) > 0) out[meta.fclQuant] = posInt(s.qty);
  }
  return out;
}

// Sum every charge bucket and flip its checked flag when it has rows (mutates `checked`).
function tallyCharges(charges, checked) {
  let total = 0;
  for (const bucket of CHARGE_BUCKETS) {
    if (charges[bucket].length) checked[bucketToCheckedFlag(bucket)] = true;
    total += charges[bucket].reduce((sum, r) => sum + num(r.chargeAmount), 0);
  }
  return total;
}

// ── builder ───────────────────────────────────────────────────────────────────

/**
 * Build the saveQuote op payload from a normalized card/booking input.
 * @returns { quote, checked, charges, insuranceQuote, bookingId }
 */
export function buildSaveQuotePayload(input = {}) {
  const {
    source = 'CARD',
    mode = MODE.OCEAN,
    containerType = LOAD.FCL,
    currency = 'USD',
    fclSizes = [],
    estimates = {},
    sections = {},
    legModes = {},
    insurance = [],
    insurancePolicyIds = [],
  } = input;

  // Stable shape: every bucket present (empty) + every checked flag false.
  const charges = Object.fromEntries(CHARGE_BUCKETS.map((b) => [b, []]));
  const checked = Object.fromEntries(CHARGE_BUCKETS.map((b) => [bucketToCheckedFlag(b), false]));
  charges.chargesFreight = freightRows({
    mode,
    containerType,
    fclSizes,
    chargeableWeight: input.chargeableWeight,
    freightRate: input.freightRate,
    freightChargeTypeId: input.freightChargeTypeId,
    isEstimate: estimates.FREIGHT,
    currency,
  });
  Object.assign(charges, sectionChargeBuckets(sections, estimates, currency, legModes));

  const insuranceQuote = (Array.isArray(insurance) ? insurance : [])
    .filter((i) => num(i.value) > 0 || i.description || i.category)
    .map(insuranceRow);
  const totalPrice =
    tallyCharges(charges, checked) +
    insuranceQuote.reduce((sum, r) => sum + num(r.premium), 0);

  const quote = {
    ...fclQuantsFor(containerType, fclSizes),
    customerId: idOrNull(input.customerId),
    mode: mode === MODE.AIR ? MODE.AIR : MODE.OCEAN, // op maps OCEAN/AIR → Sea/Air
    containerType,
    // containerSize is intentionally NOT set: the RFQ-assembly flow leaves Quote.containerSize
    // null (BB's List rejects "40"/"40ST"; the size detail rides fclQuant_*), so a saved quote
    // matches it by also leaving it null. Multi-size is fully described by fclQuant_20/40/40hq/45.
    createdByBooking: source === 'BOOKING',
    carrier: idOrNull(input.carrierId),
    locationFrom: idOrNull(input.originPortId),
    locationTo: idOrNull(input.destPortId),
    dischargePort: idOrNull(input.dischargePortId),
    vesselService: idOrNull(input.vesselServiceId),
    vendorContract: idOrNull(input.vendorContractId),
    weight: num(input.weight) || null,
    cbm: num(input.cbm) || null,
    volume: num(input.cbm) || null,
    etdOrigin: input.etdOrigin || null,
    validityFrom: input.validityFrom || null,
    validityTo: input.validityTo || null,
    // transit metadata — RFQ-assembly quotes carry these, so a saved quote should too (consistency).
    transitTime: input.transitTime ?? null,
    totalTransitDays: posInt(input.totalTransitDays) || null,
    currency,
    totalPrice: round2(totalPrice),
    isOverweight: Boolean(input.isOverweight),
    insuranceQuoteIds: (Array.isArray(insurancePolicyIds) ? insurancePolicyIds : [])
      .map(idOrNull)
      .filter((v) => v != null),
  };

  const payload = { quote, checked, charges, insuranceQuote };
  if (input.quoteId != null) payload.quoteId = idOrNull(input.quoteId); // re-save → set-replace
  if (input.bookingId != null) payload.bookingId = idOrNull(input.bookingId);
  return payload;
}
